import { BankIdLoginErrorCodes } from "@data-types";
import { LoginErrorCodes } from "@data-types/user/LoginErrorCodes";
import { MitIdErrorCodes } from "@data-types/user/MitIdErrorCodes";

interface TErrorCodes {
	errorMessage?: string;
	mobileApkLink?: string;
	errorFailedAttempts?: number;
	errorDuration?: number;
	userData?:
		| string
		| {
				activationToken: string;
		  };
}

/**
 * Login error codes map
 * - Insert here new error codes
 */
export const errorCodesMap = (error: any) =>
	new Map<number, TErrorCodes>([
		[
			LoginErrorCodes.CPRNumberNotRegistered,
			{
				errorMessage:
					"MEMBERSHIP.ERR_HANDLING.CPR_NUMBER_NOT_REGISTERED",
			},
		],
		[
			LoginErrorCodes.CPRNumberVerificationError,
			{
				errorMessage:
					"MEMBERSHIP.ERR_HANDLING.CPR_NUMBER_VERIFICATION_ERROR",
			},
		],
		[
			LoginErrorCodes.spelpausGamingExclusionActive,
			{
				errorMessage:
					"MEMBERSHIP.ERR_HANDLING.SPELPAUS_GAMING_EXCLUSION_ACTIVE",
			},
		],
		[
			LoginErrorCodes.loginExclusionCheckError,
			{ errorMessage: "LOGIN_EXCLUSION_CHECK_ERROR" },
		],
		[
			LoginErrorCodes.loginIndefiniteExclusion,
			{ errorMessage: "LOGIN_INDEFINITE_EXCLUSION" },
		],
		[
			LoginErrorCodes.loginTemporaryExclusion,
			{ errorMessage: "LOGIN_TEMPORARY_EXCLUSION" },
		],
		[
			LoginErrorCodes.playerTooYoung,
			{ errorMessage: "MEMBERSHIP.ERR_HANDLING.PLAYER_TOO_YOUNG" },
		],
		[
			LoginErrorCodes.wrongapk,
			{
				errorMessage: "WRONG_APP",
				mobileApkLink: error?.data?.mobileApkLink,
			},
		],
		[LoginErrorCodes.unableToLogin, { errorMessage: "EMAIL_LOGIN" }],
		[LoginErrorCodes.lastTryLogin, { errorMessage: "PASSWORD_LAST_TRY" }],
		[LoginErrorCodes.userLocked, { errorMessage: "USER_LOCKED" }],
		[
			LoginErrorCodes.temporaryLockedWithDurationCode,
			{
				errorMessage: "USER_TEMP_LOCK_TIME",
				errorFailedAttempts: 5,
				errorDuration: error?.data?.lockDuration,
			},
		],
		[
			LoginErrorCodes.temporaryLockCode,
			{
				errorMessage: "USER_TEMP_LOCK",
				errorFailedAttempts: 5,
				errorDuration: 30,
			},
		],
		[
			LoginErrorCodes.userNotApproved,
			{ errorMessage: "USER_NOT_VERIFIED" },
		],
		[
			LoginErrorCodes.userNotExist,
			{ errorMessage: "INVALID_PASS_OR_USERNAME_POPUP" },
		],
		[
			LoginErrorCodes.invalidPassword,
			{ errorMessage: "INVALID_PASS_OR_USERNAME_POPUP" },
		],
		[
			LoginErrorCodes.userNotVerified,
			{ errorMessage: "USER_DOCUMENTS_NOT_VERIFIED" },
		],
		[
			LoginErrorCodes.oasisGamingExclusion,
			{ errorMessage: "OASIS_GAMING_EXCLUSION" },
		],
		[
			LoginErrorCodes.dataConfirmationRequiredCode,
			{ userData: error?.data },
		],
		[
			LoginErrorCodes.lugasPlayerInWaitingPeriod,
			{ errorMessage: "LUGAS_PLAYER_IN_WAITING_PERIOD" },
		],
		[
			LoginErrorCodes.lugasPlayerActiveOnDifferentProvider,
			{ errorMessage: "LUGAS_PLAYER_ACTIVE_ON_DIFFERENT_PROVIDER" },
		],
		[
			LoginErrorCodes.lugasPlayerNotRegistered,
			{
				errorMessage:
					"MEMBERSHIP.ERR_HANDLING.LUGAS_PLAYER_NOT_REGISTERED",
			},
		],
		[
			LoginErrorCodes.lugasValidationError,
			{ errorMessage: "MEMBERSHIP.ERR_HANDLING.LUGAS_VALIDATION_ERROR" },
		],
		[
			LoginErrorCodes.lugasDown,
			{ errorMessage: "MEMBERSHIP.ERR_HANDLING.LUGAS_DOWN" },
		],
		[
			LoginErrorCodes.lugasUnspecified,
			{ errorMessage: "MEMBERSHIP.ERR_HANDLING.LUGAS_UNSPECIFIED" },
		],
		[
			LoginErrorCodes.gamesNotAllowed,
			{ errorMessage: "GAMES_NOT_ALLOWED_ERROR" },
		],
		[
			MitIdErrorCodes.tokenExchangeConfigLoadFailed,
			{ errorMessage: "MIT_TOKEN_EXCHANGE_CONFIG_LOAD_FAILED" },
		],
		[
			MitIdErrorCodes.externalProviderError,
			{ errorMessage: "MIT_EXTERNAL_PROVIDER_ERROR" },
		],
		[
			MitIdErrorCodes.authResponseNotValid,
			{ errorMessage: "MIT_AUTH_RESPONSE_NOT_VALID" },
		],
		[
			MitIdErrorCodes.tokenExchangeResponseNotValid,
			{ errorMessage: "MIT_TOKEN_EXCHANGE_RESPONSE_NOT_VALID" },
		],
		[
			MitIdErrorCodes.userWithCPRNotRegistered,
			{ errorMessage: "MIT_USER_WITH_CPR_NOT_REGISTERED" },
		],
		[
			MitIdErrorCodes.multipleUsersWithCPRDetected,
			{ errorMessage: "MIT_MULTIPLE_USERS_WITH_CPR_DETECTED" },
		],
		[
			MitIdErrorCodes.userDataDoesNotMatch,
			{ errorMessage: "MIT_USER_DATA_DOES_NOT_MATCH" },
		],
		[
			MitIdErrorCodes.userValidationUnhandledError,
			{ errorMessage: "MIT_USER_VALIDATION_UNHANDLED_ERROR" },
		],
		[
			MitIdErrorCodes.unsupportedGrantType,
			{ errorMessage: "MIT_UNSUPPORTED_GRANT_TYPE" },
		],
		[
			BankIdLoginErrorCodes.authFailedOrNotCompleted,
			{
				errorMessage:
					"specific:MEMBERSHIP.REGISTRATION.BANKID.AUTH_FAILED",
			},
		],
		[
			BankIdLoginErrorCodes.multipleUsersWithPINDetected,
			{
				errorMessage:
					"specific:MEMBERSHIP.REGISTRATION.BANKID.PIN_NOT_VALID",
			},
		],
		[
			BankIdLoginErrorCodes.authResponseNotValid,
			{
				errorMessage:
					"specific:MEMBERSHIP.REGISTRATION.BANKID.AUTH_NOT_VALID",
			},
		],
		[
			BankIdLoginErrorCodes.sessionAlreadyUsed,
			{
				errorMessage:
					"specific:MEMBERSHIP.REGISTRATION.BANKID.SESSION_ID_NOT_VALID",
			},
		],
		[
			BankIdLoginErrorCodes.userDataDoesNotMatch,
			{
				errorMessage:
					"specific:MEMBERSHIP.REGISTRATION.BANKID.USER_DATA_DONT_MATCH",
			},
		],
		[
			BankIdLoginErrorCodes.userWithPINNotRegistered,
			{
				errorMessage:
					"specific:MEMBERSHIP.REGISTRATION.BANKID.PIN_NOT_VALID",
			},
		],
		[
			BankIdLoginErrorCodes.widgetInitFailed,
			{
				errorMessage:
					"specific:MEMBERSHIP.REGISTRATION.BANKID.WIDGET_INIT_FAILED",
			},
		],
	]);
